import messages from "@/i18n/messages.json";

const SECTION_TITLE_FIELD_KEYS = {
  billingTitle: "$billingTitle",
  shippingTitle: "$shippingTitle",
};

const NAME_PLACEHOLDER_ALIASES = {
  Jean: "$firstNameExample",
  Dupont: "$lastNameExample",
  John: "$firstNameExample",
  Smith: "$lastNameExample",
};

/** Magellan hardcoded FR titles still present on non-regenerated pages. */
const HARDCODED_CONTACT_DETAILS = new Set(["Coordonnées", "Coordonnees"]);
const HARDCODED_PAYMENT_METHOD = new Set([
  "Méthode de paiement",
  "Methode de paiement",
]);
const HARDCODED_POWERED_BY = "Propulsé par";
const HARDCODED_ORDER_RECAP_ARIA_LABEL = "Récapitulatif de commande";

function getLocale() {
  return window.globalConfig?.lang || "fr";
}

function getContactFormMessages(lang = getLocale()) {
  return messages[lang]?.contactForm || messages.fr.contactForm;
}

function getLabelsMessages(lang = getLocale()) {
  return messages[lang]?.labels || messages.fr.labels;
}

export function getContactDetailsTitle(lang = getLocale()) {
  return getContactFormMessages(lang).contactDetails;
}

export function getBillingTitle(lang = getLocale()) {
  return getContactFormMessages(lang).billingTitle;
}

export function getShippingTitle(lang = getLocale()) {
  return getContactFormMessages(lang).shippingTitle;
}

export function getPaymentMethodTitle(lang = getLocale()) {
  return getLabelsMessages(lang).paymentMethod;
}

export function getFirstNameExample(lang = getLocale()) {
  return getContactFormMessages(lang).firstNameExample;
}

export function getLastNameExample(lang = getLocale()) {
  return getContactFormMessages(lang).lastNameExample;
}

/**
 * Return a shallow-cloned fields map with section titles and known hardcoded
 * name placeholders rewritten to i18n keys. Does not mutate the input
 * (window.contactFormConfig.fields stays untouched).
 */
export function normalizeContactFormTitleLabels(fields) {
  if (!fields || typeof fields !== "object") {
    return fields;
  }

  const normalized = { ...fields };

  for (const [fieldName, i18nKey] of Object.entries(SECTION_TITLE_FIELD_KEYS)) {
    if (normalized[fieldName]) {
      normalized[fieldName] = {
        ...normalized[fieldName],
        label: i18nKey,
      };
    }
  }

  for (const fieldName of Object.keys(normalized)) {
    const field = normalized[fieldName];
    const placeholder = field?.placeholder;
    if (placeholder && NAME_PLACEHOLDER_ALIASES[placeholder]) {
      normalized[fieldName] = {
        ...field,
        placeholder: NAME_PLACEHOLDER_ALIASES[placeholder],
      };
    }
  }

  return normalized;
}

/**
 * Replace Magellan hardcoded French section titles, sponsor footer and
 * recap aria-label in the page HTML with locale translations. Only touches
 * known FR defaults so Vue-managed title components and already-localized
 * (regenerated) templates are left alone.
 */
export function localizeOrderPageSectionTitles(root = document) {
  const contactDetails = getContactDetailsTitle();
  const paymentMethod = getPaymentMethodTitle();
  const labels = getLabelsMessages();

  root.querySelectorAll("label.outside-label").forEach((el) => {
    if (HARDCODED_CONTACT_DETAILS.has(el.textContent.trim())) {
      el.textContent = contactDetails;
    }
  });

  root.querySelectorAll("h2.payment-method").forEach((el) => {
    if (HARDCODED_PAYMENT_METHOD.has(el.textContent.trim())) {
      el.textContent = paymentMethod;
    }
  });

  root.querySelectorAll(".palyance-sponsor").forEach((el) => {
    const textNode = el.firstChild;
    if (
      textNode?.nodeType === Node.TEXT_NODE &&
      textNode.textContent.includes(HARDCODED_POWERED_BY)
    ) {
      textNode.textContent = textNode.textContent.replace(
        HARDCODED_POWERED_BY,
        labels.poweredBy,
      );
    }
  });

  root
    .querySelectorAll(
      `section[aria-label="${HARDCODED_ORDER_RECAP_ARIA_LABEL}"]`,
    )
    .forEach((el) => {
      el.setAttribute("aria-label", labels.orderRecapAriaLabel);
    });
}
